<template>
  <footer class="pct">
    <h2 class="pct-title">{{ title }}</h2>
    <p v-if="personName" class="pct-text">
      {{ personName }}, votre {{ personRole }}, y répond&nbsp;:
      <a v-if="email" :href="`mailto:${email}`">{{ email }}</a><template v-if="email && phone">, ou au </template><a v-if="phone" class="pct-phone" :href="`tel:${phoneHref}`">{{ phone }}</a>.
    </p>
    <p v-else class="pct-text">
      <template v-if="email">Écrivez-nous à <a :href="`mailto:${email}`">{{ email }}</a></template><template v-if="email && phone">, ou appelez le </template><template v-if="phone && !email">Appelez-nous au </template><a v-if="phone" class="pct-phone" :href="`tel:${phoneHref}`">{{ phone }}</a>.
    </p>
  </footer>
</template>

<script setup>
/**
 * À QUI S'ADRESSER, en pied d'écran public.
 *
 * Le visiteur a une question sur ce qu'il vient de lire, et personne à qui la
 * poser : il n'a pas de compte, pas de messagerie interne, pas de fiche
 * d'aide. Quand le dossier porte un conseiller, c'est LUI qu'on nomme — le
 * client a déjà eu son nom au téléphone, et l'écran devient la continuation
 * de cette conversation. Sinon, le standard.
 *
 * Discret par construction : c'est un recours, pas une action de l'écran
 * (P4 — une seule action principale). D'où le filet, la taille de texte
 * courant, et l'absence de bouton.
 */
import { computed } from 'vue'

const props = defineProps({
  title: { type: String, default: 'Une question ?' },
  /** Nom du conseiller rattaché au dossier ; absent = contact générique. */
  personName: { type: String, default: null },
  personRole: { type: String, default: 'conseiller Alertis' },
  email: { type: String, default: null },
  phone: { type: String, default: null }
})

/** Un numéro composé se lit avec des espaces et se compose sans. */
const phoneHref = computed(() => (props.phone || '').replace(/\s+/g, ''))
</script>

<style scoped>
.pct {
  margin-top: var(--spacing-8);
  padding-top: var(--spacing-6);
  border-top: 1px solid var(--color-border-light);
}

.pct-title {
  margin: 0 0 var(--spacing-1);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  line-height: 1.4;
  color: var(--color-text-primary);
}

.pct-text {
  margin: 0;
  font-size: var(--font-size-base);
  line-height: 1.6;
  color: var(--color-text-secondary);
}

/* Les liens restent DANS la phrase : le pied est un recours discret, pas une
   rangée de boutons (P28). Leur cible fait pourtant 44 px (P1).
   Un padding vertical sur un lien en ligne ne grandit pas la ligne : avec des
   lignes de 25,6 px, 12 px de padding faisaient chevaucher la zone de l'e-mail
   et celle du numéro, et remonter sur le titre (TRV-5) ; borner la zone à la
   ligne la ramenait à 24 px (SQ-7). C'est donc la LIGNE qui fait 44 px : la
   boîte des glyphes (20 px en Lexend Deca) + 12 px de part et d'autre la
   remplit exactement, sans déborder sur la ligne voisine ni sur le titre.
   Le lien reste un élément en ligne : une adresse plus large que l'écran se
   coupe où il faut (`overflow-wrap: anywhere`) et la virgule qui la suit reste
   collée au dernier caractère. */
.pct-text:has(a) {
  line-height: 44px;
}

.pct-text a {
  padding-block: 12px;
  border-radius: var(--radius-sm);
  color: var(--color-primary-dark);
  text-decoration: underline;
  text-underline-offset: 2px;
  overflow-wrap: anywhere;
}

/* L'anneau suit la zone cliquable (44 px, la hauteur de la ligne), tracé en
   dedans pour ne pas barrer la ligne voisine. Sur une adresse coupée en fin de
   ligne, le navigateur l'entoure d'un seul contour qui épouse les morceaux
   (`box-decoration-break` n'agit pas sur `outline`). */
.pct-text a:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: -2px;
}

/* Un numéro se lit d'un bloc : jamais « 04 78 90 46 » / « 30 » (constat
   SQ-10). L'adresse e-mail garde sa coupure d'urgence, un numéro tient
   toujours sur la largeur d'un téléphone. */
.pct-text a.pct-phone {
  white-space: nowrap;
  overflow-wrap: normal;
}

.pct-text a:hover {
  color: var(--color-primary-darker);
}
</style>
